import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/services/supabase";
import { formatPriceFromCents } from "@/utils/pricingUtils";

export interface StripePrice {
  id: string;
  amount: number;
  currency: string;
  billingPeriod: 'monthly' | 'yearly' | 'one-time';
}

export interface StripeProduct {
  id: string;
  name: string;
  description: string | null;
  productType: 'premium' | 'course' | 'one-time';
  prices: {
    monthly?: StripePrice;
    yearly?: StripePrice;
    oneTime?: StripePrice;
  };
}

export interface StripePricingData {
  premium: {
    monthly?: StripePrice;
    yearly?: StripePrice;
  };
  courses: {
    [key: string]: {
      monthly?: StripePrice;
      yearly?: StripePrice;
    };
  };
  oneTime: {
    [key: string]: StripePrice;
  };
  products: StripeProduct[];
}

const CACHE_KEY = 'stripe_pricing_cache';
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Normalize product name to camelCase key for consistent frontend access
 * Examples:
 *   "Leader Styling Course" → "leaderStyling"
 *   "Follower Styling Course" → "followerStyling"
 *   "Partnerwork Course" → "partnerwork"
 *   "Niveles - Klau & Ros Choreography" → "niveles"
 */
const normalizeProductName = (name: string): string => {
  // Remove common suffixes (access periods first, then product types)
  let normalized = name
    .replace(/\s*-\s*(monthly|yearly)\s*access$/i, '') // Remove "- Monthly Access" / "- Yearly Access"
    .replace(/\s*course$/i, '') // Remove "Course"
    .replace(/\s*subscription$/i, '') // Remove "Subscription"
    .replace(/\s*choreography$/i, '') // Remove "Choreography"
    .trim();
  
  // Handle special cases
  if (normalized.toLowerCase().includes('niveles')) {
    return 'niveles';
  }
  
  // Split into words, filter empty, and convert to camelCase
  const words = normalized
    .split(/[\s\-_]+/)
    .filter(word => word.length > 0)
    .map(word => word.toLowerCase());
  
  // Create camelCase: first word lowercase, rest capitalized
  if (words.length === 0) return '';
  if (words.length === 1) return words[0];
  
  return words[0] + words.slice(1).map(w => w.charAt(0).toUpperCase() + w.slice(1)).join('');
};

/**
 * Hook to fetch Stripe pricing data from Supabase
 * 
 * STRICT MODE: No fallbacks to hardcoded prices.
 * If data is unavailable, components should show "Currently unavailable"
 */
export function useStripePricing() {
  const [data, setData] = useState<StripePricingData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isReady, setIsReady] = useState(false);

  const fetchPricing = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Check cache first
      const cached = sessionStorage.getItem(CACHE_KEY);
      if (cached) {
        const { data: cachedData, timestamp } = JSON.parse(cached);
        if (Date.now() - timestamp < CACHE_DURATION) {
          setData(cachedData);
          setLoading(false);
          setIsReady(true);
          return;
        }
      }

      // Fetch products and prices
      const { data: products, error: productsError } = await supabase
        .from('stripe_products')
        .select('*')
        .eq('active', true);

      if (productsError) throw productsError;

      const { data: prices, error: pricesError } = await supabase
        .from('stripe_prices')
        .select('*')
        .eq('active', true);

      if (pricesError) throw pricesError;

      if (!products || !prices) {
        throw new Error('No pricing data available');
      }

      // Structure the data
      const pricingData: StripePricingData = {
        premium: {},
        courses: {},
        oneTime: {},
        products: [],
      };

      // Process each product
      products.forEach((product) => {
        const productPrices = prices.filter(p => p.stripe_product_id === product.stripe_product_id);
        
        const structuredProduct: StripeProduct = {
          id: product.stripe_product_id,
          name: product.name,
          description: product.description,
          productType: product.product_type,
          prices: {},
        };

        // Deterministic price selection: prefer HIGHEST amount for same billing period
        // This ensures we pick the "main" price, not a discounted/legacy one
        productPrices.forEach((price) => {
          const stripePrice: StripePrice = {
            id: price.stripe_price_id,
            amount: price.amount,
            currency: price.currency,
            billingPeriod: price.billing_period,
          };

          if (price.billing_period === 'monthly') {
            // Only replace if no existing price or this one has higher amount
            if (!structuredProduct.prices.monthly || price.amount > structuredProduct.prices.monthly.amount) {
              structuredProduct.prices.monthly = stripePrice;
            }
          } else if (price.billing_period === 'yearly') {
            if (!structuredProduct.prices.yearly || price.amount > structuredProduct.prices.yearly.amount) {
              structuredProduct.prices.yearly = stripePrice;
            }
          } else if (price.billing_period === 'one-time') {
            if (!structuredProduct.prices.oneTime || price.amount > structuredProduct.prices.oneTime.amount) {
              structuredProduct.prices.oneTime = stripePrice;
            }
          }
        });

        pricingData.products.push(structuredProduct);

        // Categorize by product type
        if (product.product_type === 'premium') {
          pricingData.premium = structuredProduct.prices;
        } else if (product.product_type === 'course') {
          // Use normalized camelCase key
          const courseKey = normalizeProductName(product.name);
          pricingData.courses[courseKey] = {
            monthly: structuredProduct.prices.monthly,
            yearly: structuredProduct.prices.yearly,
          };
        } else if (product.product_type === 'one-time') {
          // Use normalized key for one-time purchases
          const key = normalizeProductName(product.name);
          if (structuredProduct.prices.oneTime) {
            pricingData.oneTime[key] = structuredProduct.prices.oneTime;
          }
        }
      });

      // Cache the data
      sessionStorage.setItem(CACHE_KEY, JSON.stringify({
        data: pricingData,
        timestamp: Date.now(),
      }));

      setData(pricingData);
      setLoading(false);
      setIsReady(true);

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load pricing';
      console.error('[useStripePricing] Error:', errorMessage);
      setError(errorMessage);
      setLoading(false);
      setIsReady(false);
    }
  }, []);

  useEffect(() => {
    fetchPricing();
  }, [fetchPricing]);

  const refetch = useCallback(() => {
    sessionStorage.removeItem(CACHE_KEY);
    fetchPricing();
  }, [fetchPricing]);

  // Helper methods for easy access
  const getPrice = useCallback((
    productKey: string, 
    billingPeriod?: 'monthly' | 'yearly' | 'one-time'
  ): StripePrice | null => {
    if (!data) return null;

    // Check premium
    if (productKey === 'premium') {
      const period = billingPeriod === 'yearly' ? 'yearly' : 'monthly';
      return data.premium[period] || null;
    }

    // Check courses
    if (data.courses[productKey]) {
      const period = billingPeriod === 'yearly' ? 'yearly' : 'monthly';
      return data.courses[productKey][period] || null;
    }

    // Check one-time
    if (data.oneTime[productKey]) {
      return data.oneTime[productKey];
    }

    return null;
  }, [data]);

  const getPriceId = useCallback((
    productKey: string, 
    billingPeriod?: 'monthly' | 'yearly' | 'one-time'
  ): string | null => {
    const price = getPrice(productKey, billingPeriod);
    return price?.id || null;
  }, [getPrice]);

  const formatAmount = useCallback((amount: number, currency: string = 'EUR'): string => {
    return formatPriceFromCents(amount, currency);
  }, []);

  return { 
    data, 
    loading, 
    error,
    isReady,
    refetch,
    // Helper methods
    getPrice,
    getPriceId,
    formatAmount,
  };
}
